import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Table, Space, Select, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import FilterToolbar from '../components/FilterToolbar';

const { Option } = Select;

const AttendanceManagementPage = () => {
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null); // 签到时长：5, 10, 0(不定时)
  const [selectedClassMode, setSelectedClassMode] = useState('ALL'); // 签到人群：ALL, 住宿, 走读
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [filterDate, setFilterDate] = useState(null);
  const [filterClassId, setFilterClassId] = useState(undefined);
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/attendance/sessions'); // Assuming this is the correct endpoint
      if (Array.isArray(response.data)) {
        // Filter: Show ACTIVE sessions OR ENDED sessions within 10 minutes
        const now = Date.now();
        const tenMinutes = 10 * 60 * 1000;
        
        const filtered = response.data.filter(session => {
            if (String(session.status).toUpperCase() === 'ACTIVE') return true;
            if (String(session.status).toUpperCase() === 'ENDED' && session.endedAt) {
                const endedTime = new Date(session.endedAt).getTime();
                return (now - endedTime) <= tenMinutes;
            }
            return false;
        });
        setAllSessions(filtered);
        setSessions(filtered);
      } else {
        setAllSessions([]);
        setSessions([]);
        console.error("Expected array from /attendance/sessions but got:", response.data);
      }
    } catch (error) {
      message.error('获取历史签到失败');
      setAllSessions([]);
      setSessions([]);
    }
    setLoading(false);
  }, []);

  const fetchClasses = useCallback(async () => {
    try {
      // Assuming an endpoint to fetch classes the current user can manage
      const response = await api.get('/classes/manageable'); 
      if (Array.isArray(response.data)) {
        setClasses(response.data);
      } else {
        setClasses([]);
        console.error("Expected array from /classes/manageable but got:", response.data);
      }
    } catch (error) {
      message.error('获取班级列表失败');
      setClasses([]);
    }
  }, []);

  useEffect(() => {
    fetchSessions();
    fetchClasses();
    // 每10秒轮询一次签到列表，让小程序结束的签到在列表里及时消失（< 10秒）
    const pollId = setInterval(fetchSessions, 10000);
    return () => clearInterval(pollId);
  }, [fetchSessions, fetchClasses]);

  const applyFilters = (data, date, classId) => {
    let list = data || allSessions;
    if (date) {
      const dayStr = date.format('YYYY-MM-DD');
      list = list.filter(s => {
        const dt = s.endedAt || s.startedAt;
        return dt && new Date(dt).toISOString().slice(0,10) === dayStr;
      });
    }
    if (classId !== undefined) {
      if (classId === 0) {
        list = list.filter(s => s.classId === 0);
      } else {
        list = list.filter(s => s.classId === classId);
      }
    }
    setSessions(list);
  };

  const onFilterDateChange = (val) => {
    setFilterDate(val);
    applyFilters(allSessions, val, filterClassId);
  };

  const onFilterClassChange = (val) => {
    setFilterClassId(val);
    applyFilters(allSessions, filterDate, val);
  };

  const resetFilters = () => {
    setFilterDate(null);
    setFilterClassId(undefined);
    setSessions(allSessions);
  };

  const showStartSessionModal = () => {
    if (classes.length === 0) {
        message.warning('您没有可以发起签到的班级');
        return;
    }
    setSelectedClass(null);
    setSelectedDuration(5); // 默认5分钟
    setSelectedClassMode('ALL'); // 默认全部
    setIsModalVisible(true);
  };

  const handleStartSession = async () => {
    if (!selectedClass) {
      message.error('请选择一个班级');
      return;
    }
    if (selectedDuration === null) {
      message.error('请选择签到时长');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/attendance/start', { 
        classId: selectedClass,
        duration: selectedDuration, // 0 表示不定时
        classMode: selectedClassMode === 'ALL' ? null : selectedClassMode // 签到人群筛选
      });
      const { id } = response.data;
      message.success('签到已发起');
      setIsModalVisible(false);
      navigate(`/attendance/session/${id}`);
    } catch (error) {
      message.error('发起签到失败');
    }
    setLoading(false);
  };

  const handleDeleteSession = async (id) => {
    try {
      await api.delete(`/attendance/sessions/${id}`);
      message.success('签到记录已删除');
      fetchSessions();
    } catch (error) {
      message.error('删除失败，请重试');
    }
  };

  const columns = [
      { title: '班级', dataIndex: 'className', key: 'className' },
      { title: '发起人', dataIndex: 'teacherName', key: 'teacherName' },
      { title: '开始时间', dataIndex: 'startedAt', key: 'startedAt', render: (text) => new Date(text).toLocaleString() },
      { title: '结束时间', dataIndex: 'endedAt', key: 'endedAt', render: (text) => text ? new Date(text).toLocaleString() : '-' },
      { 
        title: '状态', 
        dataIndex: 'status', 
        key: 'status',
        render: (status) => {
            const s = String(status || '').toUpperCase();
            if (s === 'ACTIVE') return <span style={{ color: '#52c41a' }}>进行中</span>;
            if (s === 'ENDED') return <span style={{ color: '#f5222d' }}>已结束</span>;
            return status || '-';
        }
      },
      {
          title: '操作',
          key: 'action',
          render: (_, record) => (
              <Space size="middle">
                  {String(record.status).toUpperCase() === 'ACTIVE' ? (
                      <Button type="link" onClick={() => navigate(`/attendance/session/${record.id}`)}>进入签到</Button>
                  ) : null}
                  <Button type="link" onClick={() => navigate(`/attendance/history/${record.id}`)}>查看详情</Button>
                  <Button type="link" danger onClick={() => Modal.confirm({
                      title: '确认删除',
                      content: '删除签到将同时清除所有相关的签到记录，是否继续？',
                      onOk: () => handleDeleteSession(record.id)
                  })}>删除</Button>
              </Space>
          )
      }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Button type="primary" onClick={showStartSessionModal} loading={loading}>
          发起新的签到
        </Button>
        <FilterToolbar
          dateMode="single"
          dateValue={filterDate}
          onDateChange={onFilterDateChange}
          classOptions={[{ label: '所有班级', value: 0 }, ...classes.map(c => ({ label: c.name, value: c.id }))]}
          classValue={filterClassId}
          onClassChange={onFilterClassChange}
          onReset={resetFilters}
        />
      </div>
      <Table 
        columns={columns}
        dataSource={sessions}
        loading={loading}
        rowKey="id"
        title={() => '历史签到'}
        scroll={{ x: 900 }}
      />
      <Modal
        title="发起签到"
        open={isModalVisible}
        onOk={handleStartSession}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={loading}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>选择班级</div>
          <Select
            placeholder="请选择要发起签到的班级"
            style={{ width: '100%' }}
            onChange={value => setSelectedClass(value)}
            value={selectedClass}
            aria-label="选择签到班级"
          >
            {classes.map(c => (
              <Option key={c.id} value={c.id}>{c.name}</Option>
            ))}
          </Select>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>签到时长</div>
          <Select
            placeholder="请选择签到时长"
            style={{ width: '100%' }}
            onChange={value => setSelectedDuration(value)}
            value={selectedDuration}
            aria-label="选择签到时长"
          >
            <Option value={5}>5分钟</Option>
            <Option value={10}>10分钟</Option>
            <Option value={0}>不定时（手动结束）</Option>
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>签到人群</div>
          <Select
            placeholder="请选择签到人群"
            style={{ width: '100%' }}
            onChange={value => setSelectedClassMode(value)}
            value={selectedClassMode}
            aria-label="选择签到人群"
          >
            <Option value="ALL">全部学员</Option>
            <Option value="住宿">仅住宿学员</Option>
            <Option value="走读">仅走读学员</Option>
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default AttendanceManagementPage;
